import API from '@utilities/api';
import RafThrottle from '@utilities/raf-throttle';
import { html } from '@utilities/dom-elements';
import ScreenDimensions from '@utilities/screen-dimensions';
import apiErrorHandler from '@utilities/errors/api-error-handler';
import errorHandler from '@utilities/errors/error-handler';
import Events from '@utilities/events';
import removeAlertMessage from '@utilities/errors/remove-alert-message';
import getMaxValue from '@utilities/min-max-value/get-max-value';
import ButtonLoader from '@components/button-loader';

import {
  getChildProducts,
  getPidValue,
  // getProductOptions,
  getQuantitySelected,
} from '@utilities/product';
import etmcCartEvent from '@utilities/etmc-cart-event';

const ADDTOCART_TOP_POSITION_NAMESPACE = 'addToCartTopPosition';

const JS_HOOK_ADD_TO_CART = '[js-hook-add-to-cart]';
const JS_HOOK_PRODUCT_DETAIL = '[js-hook-product-detail]';
const JS_HOOK_SET_ITEMS = '[js-hook-set-items]';
// const JS_HOOK_BUNDLE_ITEM = '[js-hook-bundle-item]';
const JS_HOOK_BUNDLE_ITEMS_FORM = '[js-hook-bundle-items-form]';
const JS_HOOK_PRODUCT_LINE_ITEM = '[js-hook-product-line-item]';
const JS_HOOK_STICKY_BUTTON = '[js-hook-product-detail-sticky-button]';
const JS_HOOK_HEADER = '[js-hook-header]';
const JS_HOOK_ADDED_TO_CART_MODAL = '[js-hook-added-to-cart-modal]';
const JS_HOOK_GTM_DATA = '[js-hook-product-add-to-cart-data]';

const JS_HOOK_ADD_TO_CART_CONFIRMATION = 'js-hook-add-to-cart-confirmation';

const HEADER_SCROLLUP_CLASS = 'header--is-scrolled-up';
const HIDDEN_CLASS = 'u-hidden';

const MODAL_TIMER = 1000;

class AddToCart {
  constructor(element) {
    this.element = element;
    const { product, url, reload } = this.element.dataset || {};

    const gtmStringObject = document.querySelector(JS_HOOK_GTM_DATA).dataset.gtmObj;
    const ga4StringObject = document.querySelector(JS_HOOK_GTM_DATA).dataset.ga4Obj;
    this.gtmObj = gtmStringObject === '' ? false : JSON.parse(gtmStringObject);
    this.ga4Obj = ga4StringObject === '' ? false : JSON.parse(ga4StringObject);

    this.productDataString = product || '';
    this.addToCartUrl = url || '';
    this.forceReload = reload || false;
    this.pidsObj = null;
    this.buttonLoader = new ButtonLoader(this.element);
    this.addedToCartModal = document.querySelector(JS_HOOK_ADDED_TO_CART_MODAL);
    this.addToCartPid = this.element.dataset.pid;

    this.addToCartIsASet = this.element.dataset.set;

    this.header = document.querySelector(JS_HOOK_HEADER);

    this.updateButtonTopPosition();
    this.bindEvents();
  }

  /**
   * Checks whether add-to-cart happens on a Set Product(Detail).
   * If true; adds all the product ids to the pids object
   */
  checkSetItems() {
    this.pidsObj = [];

    if (this.addToCartIsASet) {
      const setProductsWrapper = this.element.closest(JS_HOOK_SET_ITEMS);
      const setProducts = [...setProductsWrapper.querySelectorAll(JS_HOOK_PRODUCT_LINE_ITEM)];

      setProducts.forEach((product) => {
        this.pidsObj.push({
          pid: product.dataset.pid,
          qty: product.dataset.quantity,
        });
      });
    }

    // Old code for set PDP
    // Might need it later

    // if (
    //   document.querySelector(JS_HOOK_SET_ITEMS) &&
    //   this.element.getAttribute('data-global') === true
    // ) {
    //   [...document.querySelectorAll(JS_HOOK_PRODUCT_DETAIL)].forEach(detail => {
    //     const id = detail.querySelector('[js-hook-product-id]');
    //     const quantitySelect = detail.querySelector('[js-hook-quantity-select]');
    //
    //     if (
    //       detail.dataset &&
    //       detail.hasAttribute('js-hook-product-set-detail') &&
    //       id &&
    //       quantitySelect
    //     ) {
    //       this.pidsObj.push({
    //         pid: id.innerText,
    //         qty: quantitySelect.value,
    //         options: getProductOptions(detail)
    //       });
    //     }
    //   });
    // }
  }

  /**
   * Updates the minicart quantity value after the customer has pressed the "Add to Cart" button
   * @param {String} data - ajax response from clicking the add to cart button
   */
  triggerSuccessEvents(data) {
    if (this.element.dataset.recommendedProduct) {
      // gtm event add_to_cart/add_to_cart_ga when add recommended product
      let recommendedGtmObj = this.element.dataset.gtmObj ? this.element.dataset.gtmObj : false;
      let recommendedGa4Obj = this.element.dataset.ga4Obj ? this.element.dataset.ga4Obj : false;

      if (recommendedGtmObj && recommendedGa4Obj) {
        recommendedGtmObj = JSON.parse(recommendedGtmObj);
        recommendedGa4Obj = JSON.parse(recommendedGa4Obj);

        for (let index = 0; index < recommendedGtmObj.length; index++) {
          const currentRecommendedGtmObj = recommendedGtmObj[index];
          const currentRecommendedGa4Obj = recommendedGa4Obj[index];

          Events.$trigger('gtm::push', { data: currentRecommendedGtmObj });
          Events.$trigger('gtm::push', { data: { ecommerce: null } });
          Events.$trigger('gtm::push', { data: currentRecommendedGa4Obj });
        }
      }

    } else {
      // default adding to cart
      const gtmStringObject = document.querySelector(JS_HOOK_GTM_DATA).dataset.gtmObj;
      const ga4StringObject = document.querySelector(JS_HOOK_GTM_DATA).dataset.ga4Obj;

      this.gtmObj = gtmStringObject === '' ? false : JSON.parse(gtmStringObject);
      this.ga4Obj = ga4StringObject === '' ? false : JSON.parse(ga4StringObject);

      Events.$trigger('gtm::push', { data: this.gtmObj });
      Events.$trigger('gtm::push', { data: { ecommerce: null } });
      Events.$trigger('gtm::push', { data: this.ga4Obj });
    }

    etmcCartEvent(data.cart.items);

    if (this.forceReload) {
      window.location.reload();
      return;
    }

    if (this.addedToCartModal) {
      const modalId = `recommendation-modal-${this.pid}`;

      setTimeout(() => {
        Events.$trigger(`modal[${modalId}]::open`);
      }, MODAL_TIMER);
    }

    if (this.addToCartIsASet) {
      const addToCartButtons = [...document.querySelectorAll(JS_HOOK_ADD_TO_CART)];

      addToCartButtons.forEach((button) => {
        const buttonNextSibling = button.nextElementSibling;

        if (buttonNextSibling?.hasAttribute(JS_HOOK_ADD_TO_CART_CONFIRMATION)) {
          button.classList.add(HIDDEN_CLASS);
          buttonNextSibling.classList.remove(HIDDEN_CLASS);
        }
      });
    }

    Events.$trigger('minicart::update', { data });
  }

  /**
   * Succes handler of Axios response
   *
   * @param {Object} data - Axios response
   * @return {undefined}
   */
  addToCartSuccess(data) {
    if (data.error) {
      this.buttonLoader.hide();
      apiErrorHandler(data);
    } else {
      this.buttonLoader.hide(true);
      removeAlertMessage();
      this.triggerSuccessEvents(data);
    }
  }

  /**
   * Process API error for addToCart
   *
   * @param {Object|string} error
   */
  addToCartError(error) {
    this.buttonLoader.hide();
    errorHandler(error);
  }

  addToCart() {
    this.buttonLoader.show();

    API.post(this.addToCartUrl, this.form)
      .then((response) => this.addToCartSuccess(response.data))
      .catch((error) => this.addToCartError(error));
  }

  /**
   * Set variables on class scope for later use
   */
  setVariables() {
    if (this.addToCartPid) {
      this.pid = getPidValue(this.element);
    }
    this.productContainer = this.element.closest(JS_HOOK_PRODUCT_DETAIL);
  }

  /**
   * Prepare add to cart form for api serialization
   */
  updateAddToCartForm(quantity) {
    this.form = {
      pid: this.pid,
      pidsObj: this.pidsObj.length > 0 ? JSON.stringify(this.pidsObj) : null,
      quantity,
    };

    const bundleItems = document.querySelector(JS_HOOK_BUNDLE_ITEMS_FORM);
    if (bundleItems) {
      this.form.childProducts = getChildProducts(this.inputs);
    }

    // Not sure what this is suppose to do, doesn't seem to be used.

    // if (!document.querySelector(JS_HOOK_BUNDLE_ITEM)) {
    //   this.form.options = getProductOptions(this.productContainer);
    // }
  }

  /**
   * Function to handle quantity from event
   *
   * @param {Number} quantity - quantity amount
   */
  handleAddToCart(quantity) {
    if (this.element.disabled) return;
    this.checkSetItems();
    this.setVariables();
    this.updateAddToCartForm(quantity);

    if (this.addToCartUrl) {
      this.addToCart();
    }
  }

  /**
   * Event handler for add to cart button
   *
   * @param {Object} event - the event object
   */
  handleButtonClick(event) {
    event.preventDefault();
    let quantity = null;

    if (this.addToCartPid) {
      quantity = getMaxValue(getQuantitySelected(this.element));
    }

    this.handleAddToCart(quantity);
  }

  // depending on header postiion > reposition add to cart
  updateButtonTopPosition() {
    if (ScreenDimensions.isTabletLandscapeAndBigger) return;

    const stickyParentEl = this.element.closest(JS_HOOK_STICKY_BUTTON);
    const headerHeight = this.header.offsetHeight;

    if (!stickyParentEl) return;

    if (html.classList.contains(HEADER_SCROLLUP_CLASS)) {
      stickyParentEl.style.transform = `translate3d(0,${headerHeight}px,0)`;
    } else {
      stickyParentEl.removeAttribute('style');
    }
  }

  /**
   * Bind all events
   */
  bindEvents() {
    this.element.addEventListener('click', (event) => this.handleButtonClick(event));

    RafThrottle.set([
      {
        element: window,
        event: 'scroll',
        namespace: ADDTOCART_TOP_POSITION_NAMESPACE,
        fn: () => this.updateButtonTopPosition(),
      },
    ]);
  }
}

export default AddToCart;
